import { useState } from "react";

import { ModuleType, type CreateModuleRequestBody, type Floor, type Module } from "@eisox/api/core-api";
import { ActionButton, Field, Loader, Modal, Plan, Select, Switch } from "@eisox/design-system";
import { isFeatureSupported } from "@eisox/device-features";
import { useAppForm } from "@eisox/form";
import { BoilerRoomIcon, PlusIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useSelector } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { moduleApi } from "~/api";
import { ExpandedGateway } from "~/components/expended-gateway";
import { VersionGate } from "~/components/version-gate";
import { floorPlanQueries } from "~/features/floor/floor-plan.queries";
import { m } from "~/paraglide/messages";
import type { EnhancedGateway } from "~/types/gateway.type";

import {
  ADD_MODULE_DEFAULT_VALUES,
  ADD_MODULE_TYPES,
  addModuleSchema,
  formToCreateBody,
  gatewayHasRelay,
  getModuleTypeLabel,
  isNiagara,
} from "../helpers";

const NAME = "AddModuleDialog";

interface AddModuleFormProps {
  buildingId: string;
  gateways: EnhancedGateway[];
  floors: Floor[];
  modules: Module[];
  onClose: () => void;
}

const AddModuleForm: React.FC<AddModuleFormProps> = ({ buildingId, gateways, floors, modules, onClose }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const createMutation = useMutation({
    meta: { toast: { action: "create" } },
    mutationFn: (body: CreateModuleRequestBody) => moduleApi.create(buildingId, body),
  });

  const form = useAppForm({
    defaultValues: ADD_MODULE_DEFAULT_VALUES,
    validators: { onSubmit: addModuleSchema },
    onSubmit: async ({ value }) => {
      const created = await createMutation.mutateAsync(formToCreateBody(value));
      await queryClient.invalidateQueries({ queryKey: moduleApi.queries.building(buildingId).queryKey });
      onClose();
      await navigate({
        to: "/buildings/$buildingId/settings/bms/modules/$moduleId",
        params: { buildingId, moduleId: created.id },
      });
    },
  });

  const type = useSelector(form.store, state => state.values.type);
  const gatewayId = useSelector(form.store, state => state.values.gatewayId);
  const gtbEnabled = useSelector(form.store, state => state.values.gtbEnabled);
  const isNiagaraType = isNiagara(type);

  const selectedGateway = gateways.find(g => g.id === gatewayId);

  const [floorId, setFloorId] = useState(() => gateways[0]?.floor.id ?? floors[0]?.id ?? "");
  // A floor may have no plan image; don't retry/refetch a missing one — it otherwise hammers the endpoint
  // and keeps the loader spinning. On failure we fall back to an empty state (gateways stay selectable via the list).
  const {
    data: plan,
    isLoading: isPlanLoading,
    isError: isPlanError,
  } = useQuery({
    ...floorPlanQueries.byId(floorId),
    enabled: !!floorId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const gatewaysOnFloor = gateways.filter(g => g.floor.id === floorId);

  // Firmware too old for BACnet addressing clears the dependent field.
  const applyGatewayConstraints = (gateway: EnhancedGateway) => {
    if (!isFeatureSupported("gateway", "BOILERROOM_BACNET_DEVICE_ID", gateway.softwareVersion))
      form.setFieldValue("bacnetDeviceId", undefined);
    if (form.getFieldValue("type") === ModuleType.ECLYPSE_RELAIS && gatewayHasRelay(modules, gateway.id))
      form.setFieldValue("type", ADD_MODULE_DEFAULT_VALUES.type);
  };

  const selectGateway = (gateway: EnhancedGateway) => {
    form.setFieldValue("gatewayId", gateway.id);
    setFloorId(gateway.floor.id);
    applyGatewayConstraints(gateway);
  };

  const typeOptions = ADD_MODULE_TYPES.map(t => ({
    value: t,
    name: getModuleTypeLabel(t),
    disabled: t === ModuleType.ECLYPSE_RELAIS && gatewayHasRelay(modules, gatewayId),
  }));
  const gatewayOptions = gateways.map(g => ({ value: g.id, name: `${g.uid} - ${g.name}` }));
  const floorOptions = floors.map(f => ({ value: f.id, name: f.name }));

  return (
    <form
      className="flex w-full flex-col text-base"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <Modal.Header title={m.module_add_title()} subtitle="" icon={<BoilerRoomIcon />}>
          <Modal.Close asChild>
            <ActionButton variant="cancel">{m.cancel()}</ActionButton>
          </Modal.Close>
          <form.SubmitButton label={m.add()} icon={<PlusIcon />} />
        </Modal.Header>
        <div className="h-163 min-h-0 gap-5 flex">
          <div className="w-87.5 gap-2.5 flex flex-col overflow-y-auto">
            <form.AppField name="type">
              {field => (
                <field.Select
                  label={m.type()}
                  multiple={false}
                  options={typeOptions}
                  searchInput={false}
                  onValueChange={value => {
                    if (typeof value !== "string") return;
                    const nextType = value as ModuleType;
                    // BACnet addressing and PLC credentials only apply to Eclypse.
                    if (nextType !== ModuleType.ECLYPSE_BOILER_ROOM) form.setFieldValue("bacnetDeviceId", undefined);
                    form.setFieldValue("isProxyExternal", false);
                    if (isNiagara(nextType)) {
                      form.setFieldValue("userName", "");
                      form.setFieldValue("password", "");
                    }
                  }}
                  classNames={{ control: "w-full" }}
                />
              )}
            </form.AppField>
            <form.AppField name="gatewayId">
              {field => (
                <field.Select
                  label={m.gateway()}
                  placeholder={m.select_gateway()}
                  multiple={false}
                  options={gatewayOptions}
                  onValueChange={value => {
                    if (typeof value !== "string") return;
                    const gateway = gateways.find(g => g.id === value);
                    if (gateway) {
                      setFloorId(gateway.floor.id);
                      applyGatewayConstraints(gateway);
                    }
                  }}
                  classNames={{ control: "w-full" }}
                />
              )}
            </form.AppField>
            <div className="gap-2.5 flex items-start">
              <form.AppField name="url">
                {field => (
                  <field.TextField
                    classNames={{ root: "flex-1", control: "w-full" }}
                    label={m.module_add_ip()}
                    placeholder={m.module_add_ip_placeholder()}
                    onFocus={() => form.setFieldValue("bacnetDeviceId", undefined)}
                  />
                )}
              </form.AppField>
              {type === ModuleType.ECLYPSE_BOILER_ROOM && (
                <>
                  <p className="mt-8 text-base text-primary-dark-gray">{m.module_add_or()}</p>
                  <VersionGate
                    devices={[
                      {
                        type: "gateway",
                        current: selectedGateway?.softwareVersion,
                        feature: "BOILERROOM_BACNET_DEVICE_ID",
                      },
                    ]}
                  >
                    {({ disabled }) => (
                      // Wrap in a real DOM node so the VersionGate tooltip (Radix Trigger asChild) has an
                      // element to bind to — a disabled input alone emits no hover events.
                      <div className="flex-1">
                        <form.AppField name="bacnetDeviceId">
                          {field => (
                            <field.NumberField
                              classNames={{ control: "w-full" }}
                              label={m.module_add_bacnet()}
                              disabled={disabled}
                              inputProps={{
                                placeholder: m.module_add_bacnet_placeholder(),
                                onFocus: () => form.setFieldValue("url", ""),
                              }}
                            />
                          )}
                        </form.AppField>
                      </div>
                    )}
                  </VersionGate>
                </>
              )}
            </div>
            <form.AppField name="port">
              {field => (
                <field.NumberField
                  classNames={{ control: "w-full" }}
                  label={m.module_add_port()}
                  format={{ useGrouping: false }}
                />
              )}
            </form.AppField>
            {!isNiagaraType && (
              <>
                <form.AppField name="userName">
                  {field => (
                    <field.TextField
                      classNames={{ control: "w-full" }}
                      label={m.module_add_user()}
                      placeholder={m.module_add_user_placeholder()}
                      autoComplete="new-password"
                    />
                  )}
                </form.AppField>
                <form.AppField name="password">
                  {field => (
                    <field.PasswordField
                      classNames={{ control: "w-full" }}
                      label={m.module_add_password()}
                      placeholder={m.module_add_password_placeholder()}
                      autoComplete="new-password"
                    />
                  )}
                </form.AppField>
              </>
            )}
            <div className="gap-2.5 flex flex-col">
              <label className="flex items-center justify-between">
                <span className="font-medium text-primary-dark-blue">{m.module_add_gtb()}</span>
                <form.AppField name="gtbEnabled">
                  {field => (
                    <Switch
                      checked={field.state.value}
                      onCheckedChange={checked => field.handleChange(checked ?? false)}
                    />
                  )}
                </form.AppField>
              </label>
              {gtbEnabled && (
                <>
                  <form.AppField name="name">
                    {field => (
                      <field.TextField
                        classNames={{ control: "w-full" }}
                        label={m.name()}
                        placeholder={m.module_add_name_placeholder()}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="proxyUserName">
                    {field => (
                      <field.TextField
                        classNames={{ control: "w-full" }}
                        label={isNiagaraType ? m.module_add_operator_user_niagara() : m.module_add_operator_user()}
                        placeholder={m.module_add_operator_user_placeholder()}
                        autoComplete="new-password"
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="proxyPassword">
                    {field => (
                      <field.PasswordField
                        classNames={{ control: "w-full" }}
                        label={m.password()}
                        placeholder={m.module_add_operator_password_placeholder()}
                        autoComplete="new-password"
                      />
                    )}
                  </form.AppField>
                  {isNiagaraType && (
                    <label className="flex items-center justify-between">
                      <span className="font-medium text-primary-dark-blue">{m.bms_open_in_new_tab()}</span>
                      <form.AppField name="isProxyExternal">
                        {field => (
                          <Switch
                            checked={field.state.value}
                            onCheckedChange={checked => field.handleChange(checked ?? false)}
                          />
                        )}
                      </form.AppField>
                    </label>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="gap-2.5 flex flex-1 flex-col">
            <Field.Root>
              <Field.Label>{m.module_add_level()}</Field.Label>
              <Select.Root multiple={false} value={floorId} onValueChange={setFloorId}>
                <Select.Trigger className="w-75">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Viewport>
                    {floorOptions.map(f => (
                      <Select.Item key={f.value} value={f.value}>
                        {f.name}
                      </Select.Item>
                    ))}
                  </Select.Viewport>
                </Select.Content>
              </Select.Root>
            </Field.Root>
            <div className="relative flex-1">
              {isPlanLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Loader />
                </div>
              ) : plan ? (
                <Plan.Root>
                  <Plan.Controls className="top-2.5 left-2.5 absolute z-1" />
                  <Plan.Wrapper className="h-full! w-full!" plan={plan}>
                    {gatewaysOnFloor.map(g => (
                      <Plan.Item key={g.id} position={{ x: g.floor.x, y: g.floor.y }} origin="bottom center">
                        <ExpandedGateway
                          selected={g.id === gatewayId}
                          onClick={e => {
                            e.preventDefault();
                            selectGateway(g);
                          }}
                        >
                          {g.uid} - {g.name}
                        </ExpandedGateway>
                      </Plan.Item>
                    ))}
                  </Plan.Wrapper>
                </Plan.Root>
              ) : (
                <div className="flex h-full items-center justify-center text-primary-dark-blue/60 italic">
                  {isPlanError ? m.module_add_plan_unavailable() : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </form.AppForm>
    </form>
  );
};

AddModuleForm.displayName = `${NAME}.Form`;

interface AddModuleDialogProps extends Modal.RootProps {
  buildingId: string;
  gateways: EnhancedGateway[];
  floors: Floor[];
  modules: Module[];
  children?: React.ReactNode;
}

export const AddModuleDialog: React.FC<AddModuleDialogProps> = ({
  buildingId,
  gateways,
  floors,
  modules,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  return (
    <Modal.Root open={open} onOpenChange={setOpen}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content className="w-350">
        <AddModuleForm
          buildingId={buildingId}
          gateways={gateways}
          floors={floors}
          modules={modules}
          onClose={() => setOpen(false)}
        />
      </Modal.Content>
    </Modal.Root>
  );
};

AddModuleDialog.displayName = NAME;

export type { AddModuleDialogProps };
